import { useMemo } from "react";
import { useAppSelector } from "../../framework/redux/hooks";
import {
    makeSelectCalculatedWorkHoursAmountByDate,
    makeSelectCalculatedWorkHoursAmountByDateGroupedBySalaryRowTypeId,
    makeSelectCalculatedWorkHoursAmountByDateGroupedByWorkShiftIdAndDate,
    makeSelectCalculatedWorkHoursAmountByWorkShiftIds,
    makeSelectCalculatedWorkHoursAmountByWorkShiftIdsGroupedByCategory,
    makeSelectCalculatedWorkHoursByDate,
    makeSelectCalculatedWorkHoursByWorkShiftIds,
    makeSelectNormalWorkHoursAmountByDate,
    makeSelectNormalWorkHoursAmountByDateGroupedByWorkShiftIdAndDate,
    makeSelectNormalWorkHoursAmountByWorkShiftIds,
    makeSelectNormalWorkHoursAmountByWorkShiftIdsGroupedByCategory,
    makeSelectNormalWorkHoursByDate,
    makeSelectNormalWorkHoursByWorkShiftIds,
} from "../../framework/redux/slices/workHoursSlice";
import {
    dailyAllowanceCategories,
    deductionHourBookingCategories,
    paidHourBookingCategories,
    fillingHourCategories,
} from "../../models/workHours/workHoursItem";

export const useSelectCalculatedWorkHoursAmountBySalaryRowTypeId = (
    rangeStart: string,
    rangeEnd: string
) => {
    const state = useAppSelector((state) => state.workHours);

    const selectWorkHoursAmountByDate = useMemo(
        makeSelectCalculatedWorkHoursAmountByDateGroupedBySalaryRowTypeId,
        []
    );
    return selectWorkHoursAmountByDate(state, rangeStart, rangeEnd);
};

export const useSelectDailyAllowancesByDate = (
    rangeStart: string,
    rangeEnd: string,
    normalOnly = false
) => {
    const state = useAppSelector((state) => state.workHours);
    const categories = useMemo(() => dailyAllowanceCategories(), []);
    const selectWorkHoursByDate = useMemo(
        normalOnly
            ? makeSelectNormalWorkHoursByDate
            : makeSelectCalculatedWorkHoursByDate,
        [normalOnly]
    );

    return selectWorkHoursByDate(state, rangeStart, rangeEnd, categories);
};

export const useSelectDailyAllowancesByWorkShiftIds = (
    workShiftIds: string[],
    normalOnly = false
) => {
    const state = useAppSelector((state) => state.workHours);
    const categories = useMemo(() => dailyAllowanceCategories(), []);
    const selectWorkHoursByWorkShiftIds = useMemo(
        normalOnly
            ? makeSelectNormalWorkHoursByWorkShiftIds
            : makeSelectCalculatedWorkHoursByWorkShiftIds,
        [normalOnly]
    );

    return selectWorkHoursByWorkShiftIds(
        state,
        workShiftIds,
        categories
    );
};

export const useSelectPaidWorkHoursAmountByDate = (
    rangeStart: string,
    rangeEnd: string,
    normalOnly = true,
    withoutShiftIdOnly = false
) => {
    const state = useAppSelector((state) => state.workHours);

    const categories = useMemo(() => paidHourBookingCategories(), []);
    const selectWorkHoursAmountByDate = useMemo(
        normalOnly
            ? makeSelectNormalWorkHoursAmountByDate
            : makeSelectCalculatedWorkHoursAmountByDate,
        [normalOnly]
    );
    return selectWorkHoursAmountByDate(
        state,
        rangeStart,
        rangeEnd,
        categories,
        withoutShiftIdOnly
    );
};

export const useSelectPaidWorkHoursAmountByWorkShiftIds = (
    workShiftIds: string[],
    normalOnly = true
) => {
    const state = useAppSelector((state) => state.workHours);

    const categories = useMemo(() => paidHourBookingCategories(), []);
    const selectWorkHoursAmountByWorkShiftIds = useMemo(
        normalOnly
            ? makeSelectNormalWorkHoursAmountByWorkShiftIds
            : makeSelectCalculatedWorkHoursAmountByWorkShiftIds,
        [normalOnly]
    );
    return selectWorkHoursAmountByWorkShiftIds(state, workShiftIds, categories);
};

export const useSelectPaidWorkHoursAmountByWorkShiftIdAndDate = (
    rangeStart: string,
    rangeEnd: string,
    normalOnly = true
) => {
    const state = useAppSelector((state) => state.workHours);

    const categories = useMemo(() => paidHourBookingCategories(), []);
    const selectWorkHoursAmountByDate = useMemo(
        normalOnly
            ? makeSelectNormalWorkHoursAmountByDateGroupedByWorkShiftIdAndDate
            : makeSelectCalculatedWorkHoursAmountByDateGroupedByWorkShiftIdAndDate,
        [normalOnly]
    );
    return selectWorkHoursAmountByDate(state, rangeStart, rangeEnd, categories);
};

export const useSelectDeductionWorkHoursAmountByWorkShiftIdsGroupedByCategory = (
    workShiftIds: string[],
    normalOnly = true
) => {
    const state = useAppSelector((state) => state.workHours);

    const categories = useMemo(() => deductionHourBookingCategories(), []);
    const select = useMemo(
        normalOnly
            ? makeSelectNormalWorkHoursAmountByWorkShiftIdsGroupedByCategory
            : makeSelectCalculatedWorkHoursAmountByWorkShiftIdsGroupedByCategory,
        [normalOnly]
    );
    return select(state, workShiftIds, categories);
};

export const useSelectFillingHoursAmountByDate = (
    rangeStart: string,
    rangeEnd: string,
    normalOnly = true
) => {
    const state = useAppSelector((state) => state.workHours);

    const categories = useMemo(() => fillingHourCategories(), []);
    const selectWorkHoursAmountByDate = useMemo(
        normalOnly
            ? makeSelectNormalWorkHoursAmountByDate
            : makeSelectCalculatedWorkHoursAmountByDate,
        [normalOnly]
    );
    return selectWorkHoursAmountByDate(state, rangeStart, rangeEnd, categories);
};
